import { cn } from "@/lib/utils";

/** A shimmering block in the skeleton colour. Respects prefers-reduced-motion (index.css). */
export const Bone = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div aria-hidden="true" className={cn("he-skeleton rounded-md", className)} style={style} />
);

type Variant = "table" | "dashboard" | "editor" | "form" | "detail";

/**
 * Content-shaped skeleton for the content well while a page chunk or its
 * first data loads. The sidebar and top bar stay mounted around it.
 */
export const ContentSkeleton = ({
  variant = "table",
  label = "Loading page",
}: {
  variant?: Variant;
  label?: string;
}) => (
  <div aria-busy="true" aria-label={label} role="status" className="space-y-6">
    <span className="sr-only">{label}…</span>
    <div className="flex items-end justify-between gap-4">
      <div className="space-y-2">
        <Bone className="h-8 w-64" />
        <Bone className="h-4 w-96 max-w-full" />
      </div>
      <Bone className="h-10 w-28 rounded-xl" />
    </div>

    {variant === "dashboard" && (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-4 space-y-3">
                <Bone className="h-4 w-24" />
                <Bone className="h-7 w-20" />
                <Bone className="h-3 w-32" />
              </div>
            ))}
          </div>
          <div className="rounded-2xl border bg-card p-4 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Bone className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Bone className="h-4 w-56 max-w-full" />
                  <Bone className="h-3 w-80 max-w-full" />
                </div>
                <Bone className="h-9 w-24 rounded-xl" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-4 space-y-3">
          <Bone className="h-5 w-32" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Bone key={i} className="h-4 w-full" />
          ))}
        </div>
      </div>
    )}

    {(variant === "table" || variant === "detail") && (
      <div className="rounded-2xl border bg-card">
        <div className="flex items-center gap-3 border-b p-4">
          <Bone className="h-9 w-72 rounded-xl" />
          <Bone className="h-9 w-40 rounded-xl" />
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 border-b last:border-0 px-4 py-3">
            <Bone className="h-12 w-9 rounded" />
            <div className="flex-1 space-y-2">
              <Bone className="h-4 w-60 max-w-full" />
              <Bone className="h-3 w-40" />
            </div>
            <Bone className="h-4 w-24 hidden md:block" />
            <Bone className="h-4 w-16 hidden md:block" />
            <Bone className="h-8 w-16 rounded-lg" />
          </div>
        ))}
      </div>
    )}

    {variant === "editor" && (
      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_320px]">
        <div className="rounded-2xl border bg-card p-3 space-y-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <Bone key={i} className="h-12 w-full rounded-xl" />
          ))}
        </div>
        <div className="rounded-2xl border bg-card p-5 space-y-4">
          <Bone className="h-10 w-full rounded-xl" />
          <Bone className="h-20 w-full rounded-xl" />
          <Bone className="h-72 w-full rounded-xl" />
        </div>
        <div className="hidden xl:block rounded-2xl border bg-card p-4 space-y-3">
          <Bone className="h-5 w-24" />
          <Bone className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )}

    {variant === "form" && (
      <div className="rounded-2xl border bg-card p-6 space-y-5 max-w-3xl">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Bone className="h-4 w-28" />
            <Bone className="h-10 w-full rounded-xl" />
          </div>
        ))}
      </div>
    )}
  </div>
);

/** Used only before the shell exists (checking the session). */
export const FullPageSpinner = ({ label = "Loading" }: { label?: string }) => (
  <div
    className="flex h-screen w-full items-center justify-center bg-background"
    role="status"
    aria-busy="true"
    aria-label={label}
  >
    <div className="flex flex-col items-center gap-4">
      <img src="/hriatna-eng-192x192.png" alt="" width={40} height={40} className="opacity-90" />
      <div className="h-1 w-40 overflow-hidden rounded-full bg-[hsl(var(--skeleton))]">
        <div className="he-skeleton h-full w-full" />
      </div>
      <span className="text-sm text-ink-3">{label}…</span>
    </div>
  </div>
);

/** Pick a skeleton shape from the URL so the placeholder matches the page that's coming. */
export const skeletonVariantFor = (pathname: string): Variant => {
  if (pathname === "/admin/today") return "dashboard";
  if (/\/(edit|new)$/.test(pathname)) return pathname.startsWith("/admin/books") ? "editor" : "form";
  if (/\/admin\/(analytics|finance$|ai\/health|notifications$)/.test(pathname)) return "dashboard";
  if (/\/admin\/[^/]+\/[^/]+\/[^/]+/.test(pathname)) return "detail";
  return "table";
};
